import React, { useState } from 'react';
import { CheckCircle, ArrowRight, ArrowLeft, Loader2, Shield, Phone, Mail } from 'lucide-react';
import api from '@/api/api';

const EligibilityTest = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    postal_code: '',
    housing_type: '',
    surface: '',
    construction_year: '',
    current_heating: '',
    occupants: '',
    fiscal_reference_income: '',
    work_types: [],
  });

  const [errors, setErrors] = useState({});

  const totalSteps = 4;

  const housingTypes = [
    { value: 'maison', label: 'Maison', emoji: '🏠', description: 'Maison individuelle' },
    { value: 'appartement', label: 'Appartement', emoji: '🏢', description: 'Appartement en immeuble' },
  ];

  const occupantsOptions = [
    { value: '1', label: '1', emoji: '🙍', description: 'personne' },
    { value: '2', label: '2', emoji: '👫', description: 'personnes' },
    { value: '3', label: '3', emoji: '👨‍👩‍👧', description: 'personnes' },
    { value: '4', label: '4', emoji: '👨‍👩‍👧‍👦', description: 'personnes' },
    { value: '5+', label: '5 +', emoji: '👨‍👩‍👧‍👦', description: 'personnes' },
  ];

  const heatingTypes = [
    { value: 'fioul', label: 'Fioul', emoji: '🛢️', description: 'Chaudière fioul' },
    { value: 'gaz', label: 'Gaz', emoji: '🔥', description: 'Chaudière gaz' },
    { value: 'electrique', label: 'Électrique', emoji: '⚡', description: 'Convecteurs / radiateurs' },
    { value: 'bois', label: 'Bois', emoji: '🌲', description: 'Poêle / chaudière bois' },
    { value: 'autre', label: 'Autre', emoji: '❓', description: 'Autre type' },
  ];

  const workTypes = [
    { value: 'pac_air_eau', label: 'Pompe à Chaleur Air-Eau', emoji: '🌡️' },
    { value: 'pac_air_air', label: 'Pompe à Chaleur Air-Air', emoji: '❄️' },
    { value: 'ballon_solaire', label: 'Ballon Solaire', emoji: '☀️' },
    { value: 'ballon_thermodynamique', label: 'Ballon Thermodynamique', emoji: '💧' },
    { value: 'isolation_combles', label: 'Isolation des Combles', emoji: '🏠' },
    { value: 'isolation_planchers', label: 'Isolation Planchers Bas', emoji: '👇' },
    { value: 'isolation_murs', label: 'Isolation Murs Intérieurs', emoji: '🧱' },
    { value: 'ventilation', label: 'Ventilation VMC', emoji: '🌀' },
  ];

  // Avantages et inconvénients pour chaque solution
  const workTypesDetails = {
    pac_air_eau: {
      avantages: [
        'Aides jusqu\'à 15 990€',
        'Chauffage + eau chaude',
        '70% d\'économies sur facture',
        'Compatible radiateurs existants',
        'Fonctionne jusqu\'à -20°C'
      ],
      inconvenients: [
        'Nécessite un circuit d\'eau',
        'Vérifier compatibilité radiateurs',
        'Installation plus complexe',
        'Coût initial élevé sans aides'
      ]
    },
    pac_air_air: {
      avantages: [
        'Chauffage + climatisation',
        'Installation rapide (1 jour)',
        'Pas de circuit d\'eau nécessaire',
        'Idéale pour chauffage électrique',
        'Très économique à l\'usage'
      ],
      inconvenients: [
        'Aides limitées (834€)',
        'Pas de production eau chaude',
        'Nécessite une unité par pièce',
        'Entretien annuel obligatoire'
      ]
    },
    ballon_solaire: {
      avantages: [
        'Aides jusqu\'à 4 140€',
        '70% eau chaude gratuite',
        'Énergie 100% renouvelable',
        'Très économique à l\'usage',
        'Durée de vie 20-25 ans'
      ],
      inconvenients: [
        'Nécessite toit orienté sud',
        'Pente de toit 30-45° requise',
        'Appoint électrique nécessaire',
        'Coût initial élevé',
        'Installation sur toiture'
      ]
    },
    ballon_thermodynamique: {
      avantages: [
        'Aides jusqu\'à 1 300€',
        'Divise facture par 3',
        '70% d\'économies',
        'Installation simple',
        'Rentable en 3-4 ans'
      ],
      inconvenients: [
        'Nécessite espace 10m² min',
        'Pièce non chauffée requise',
        'Niveau sonore 35-45 dB',
        'Moins efficace par grand froid'
      ]
    },
    isolation_combles: {
      avantages: [
        'Aides jusqu\'à 3 700€',
        '30% d\'économies chauffage',
        'Travaux rapides (1-2 jours)',
        'Retour sur investissement rapide',
        'Confort été et hiver'
      ],
      inconvenients: [
        'Perte de hauteur sous combles',
        'Dépose isolant ancien si existant',
        'Accès combles nécessaire'
      ]
    },
    isolation_planchers: {
      avantages: [
        'Aides jusqu\'à 3 700€',
        '10% d\'économies chauffage',
        'Supprime sensation sol froid',
        'Pas de perte de hauteur habitable',
        'Amélioration acoustique'
      ],
      inconvenients: [
        'Nécessite cave/sous-sol accessible',
        'Hauteur minimum 1,60m requise',
        'Réduction hauteur sous cave'
      ]
    },
    isolation_murs: {
      avantages: [
        'Aides jusqu\'à 5 550€',
        '20-25% d\'économies',
        'Pas de modification façade',
        'Isolation phonique renforcée',
        'Finition impeccable'
      ],
      inconvenients: [
        'Perte 10-15cm par mur',
        'Radiateurs à déplacer',
        'Pièce inutilisable pendant travaux',
        'Durée travaux 2-5 jours/pièce'
      ]
    },
    ventilation: {
      avantages: [
        'Aides jusqu\'à 2 700€',
        '15-20% d\'économies chauffage',
        'Air pur en permanence',
        'Récupération 90% chaleur',
        'Réduit allergies et humidité'
      ],
      inconvenients: [
        'Nécessite combles accessibles',
        'Installation gaines complexe',
        'Entretien filtres 2x/an',
        'Idéal en construction/rénovation totale'
      ]
    }
  };

  const handleInputChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleWorkTypeToggle = (value) => {
    const current = formData.work_types;
    if (current.includes(value)) {
      handleInputChange('work_types', current.filter((v) => v !== value));
    } else {
      handleInputChange('work_types', [...current, value]);
    }
  };

  const validateStep = (currentStep) => {
    const newErrors = {};

    if (currentStep === 1) {
      if (!formData.postal_code || !/^\d{5}$/.test(formData.postal_code)) {
        newErrors.postal_code = 'Code postal valide requis';
      }
      if (!formData.housing_type) newErrors.housing_type = 'Sélectionnez un type de logement';
      if (!formData.surface || formData.surface < 20) newErrors.surface = 'Surface valide requise (min 20m²)';
    }

    if (currentStep === 2) {
      if (!formData.fiscal_reference_income || formData.fiscal_reference_income < 0) {
        newErrors.fiscal_reference_income = 'Revenu fiscal requis';
      }
      if (!formData.occupants) newErrors.occupants = 'Sélectionnez le nombre d\'occupants';
    }

    if (currentStep === 3) {
      if (!formData.current_heating) newErrors.current_heating = 'Sélectionnez un type de chauffage';
      if (formData.work_types.length === 0) {
        newErrors.work_types = 'Sélectionnez au moins un type de travaux';
      }
    }

    if (currentStep === 4) {
      if (!formData.name) newErrors.name = 'Le nom est requis';
      if (!formData.email) newErrors.email = 'L\'email est requis';
      if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email invalide';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      if (step < totalSteps) {
        setStep(step + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        handleSubmit();
      }
    }
  };

  const handlePrev = () => {
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    // Google Analytics event
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'simulation_pac_complete', {
        'event_category': 'PAC_Conversion',
        'event_label': 'Simulateur_Complete',
        'value': 15000
      });
    }
	// Facebook Pixel event
    if (window.fbq) {
      window.fbq('track', 'Lead', {
        content_name: 'Simulation PAC Air-Eau',
        content_category: 'Renovation_Energetique',
        value: 15000,
        currency: 'EUR'
      });
    }
    setLoading(true);
    try {
      const data = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        housing_type: formData.housing_type,
        surface: parseInt(formData.surface),
        construction_year: parseInt(formData.construction_year) || 2000,
        current_heating: formData.current_heating,
        occupants: formData.occupants === '5+' ? 5 : parseInt(formData.occupants),
        fiscal_reference_income: parseInt(formData.fiscal_reference_income),
        postal_code: formData.postal_code,
        work_types: formData.work_types,
      };

      const response = await api.testEligibility(data);
      setResult(response);
	        // Track GA event
      if (window.gtag) {
        window.gtag('event', 'simulator_completed', {
          event_category: 'Eligibility',
          total_aids: response.total_aids
        });
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting eligibility test:', error);
      const errorMessage = error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-white py-12" data-testid="eligibility-result">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Header avec logos */}
            <div className="flex justify-center gap-8 mb-12">
              <img src="/images/qualipac-logo-small.webp" alt="QualiPAC RGE" className="h-16 w-auto" width="132" height="96" />
              <img src="/images/qualibat-logo-small.webp" alt="Qualibat RGE" className="h-16 w-auto" width="132" height="96" />
            </div>

            <div className="text-center mb-12">
              <div className="inline-block w-24 h-24 bg-[#2D9A68] text-white rounded-full flex items-center justify-center text-5xl mb-6">
                ✓
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Félicitations {result.name} !
              </h1>
              <p className="text-2xl text-gray-600 mb-4">
                Vous êtes éligible aux aides
              </p>
            </div>

            {/* Montant total */}
            <div className="bg-[#2D9A68] text-white rounded-2xl p-12 mb-8 text-center">
              <p className="text-xl mb-3">💰 Montant total des aides</p>
              <p className="text-7xl font-black mb-3">{result.total_aids.toLocaleString()}€</p>
              <p className="text-xl">pour vos travaux de rénovation énergétique</p>
            </div>

            {/* Catégorie de revenus ANAH */}
            <div className="mb-8">
              <div className={`inline-flex items-center gap-3 px-8 py-4 rounded-2xl border-4 ${
                result.income_category === 'tres_modeste' ? 'bg-blue-500/10 border-blue-500' :
                result.income_category === 'modeste' ? 'bg-yellow-500/10 border-yellow-500' :
                result.income_category === 'intermediaire' ? 'bg-purple-500/10 border-purple-500' :
                'bg-pink-500/10 border-pink-500'
              }`}>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold text-white ${
                  result.income_category === 'tres_modeste' ? 'bg-blue-500' :
                  result.income_category === 'modeste' ? 'bg-yellow-500' :
                  result.income_category === 'intermediaire' ? 'bg-purple-500' :
                  'bg-pink-500'
                }`}>
                  {result.income_category === 'tres_modeste' ? '🔵' :
                   result.income_category === 'modeste' ? '🟡' :
                   result.income_category === 'intermediaire' ? '🟣' :
                   '🌸'}
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Votre profil ANAH</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {result.income_category === 'tres_modeste' ? 'Bleu - Très précaire' :
                     result.income_category === 'modeste' ? 'Jaune - Précaire' :
                     result.income_category === 'intermediaire' ? 'Violet - Intermédiaire' :
                     'Rose - Classic'}
                  </p>
                </div>
              </div>
            </div>

            {/* Détail des aides */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white border-4 border-[#2D9A68] rounded-2xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">MaPrimeRénov'</h3>
                  <div className="w-12 h-12 bg-[#2D9A68] text-white rounded-full flex items-center justify-center text-2xl">
                    {result.ma_prime_renov_eligible ? '✓' : '✗'}
                  </div>
                </div>
                <p className="text-5xl font-black text-[#2D9A68] mb-4">
                  {result.ma_prime_renov_amount.toLocaleString()}€
                </p>
                <p className={`text-lg font-semibold ${
                  result.income_category === 'tres_modeste' ? 'text-blue-600' :
                  result.income_category === 'modeste' ? 'text-yellow-600' :
                  result.income_category === 'intermediaire' ? 'text-purple-600' :
                  'text-pink-600'
                }`}>
                  {result.ma_prime_renov_eligible
                    ? `Profil ${result.income_category === 'tres_modeste' ? 'Bleu' :
                       result.income_category === 'modeste' ? 'Jaune' :
                       result.income_category === 'intermediaire' ? 'Violet' :
                       'Rose'} ANAH`
                    : 'Non éligible selon vos revenus'}
                </p>
              </div>

              <div className="bg-white border-4 border-[#2F5BA0] rounded-2xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">Prime CEE</h3>
                  <div className="w-12 h-12 bg-[#2F5BA0] text-white rounded-full flex items-center justify-center text-2xl">
                    {result.cee_eligible ? '✓' : '✗'}
                  </div>
                </div>
                <p className="text-5xl font-black text-[#2F5BA0] mb-4">
                  {result.cee_amount.toLocaleString()}€
                </p>
                <p className="text-gray-600">
                  {result.cee_eligible ? 'Prime énergie accordée' : 'Non éligible'}
                </p>
              </div>
            </div>

            {/* Recommandations */}
            {result.recommendations && result.recommendations.length > 0 && (
              <div className="bg-[#E6A835]/10 border-2 border-[#E6A835] rounded-2xl p-8 mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">✨ Nos recommandations</h3>
                <ul className="space-y-4">
                  {result.recommendations.map((rec, index) => (
                    <li key={index} className="flex items-start gap-3 text-lg">
                      <span className="text-[#2D9A68] text-2xl mt-1">✓</span>
                      <span className="text-gray-700">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CTA */}
            <div className="text-center">
              <p className="text-gray-600 mb-8 text-lg">
                📧 Un email récapitulatif a été envoyé à <strong>{result.email}</strong>
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <a
                  href="tel:0188617508"
                  className="bg-[#2D9A68] text-white px-10 py-5 rounded-xl font-bold text-xl hover:bg-[#237852] transition-all"
                  data-testid="result-call-button"
                >
                  📞 01 88 61 75 08
                </a>
                <a
                  href="/contact"
                  className="bg-[#2F5BA0] text-white px-10 py-5 rounded-xl font-bold text-xl hover:bg-[#1e3a66] transition-all"
                  data-testid="result-contact-button"
                >
                  Demander un devis
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-8" data-testid="eligibility-form">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header avec logos */}
          <div className="flex justify-center items-center gap-8 mb-8">
            <img src="/images/qualipac-logo-small.webp" alt="QualiPAC RGE" className="h-12 md:h-16 w-auto" width="132" height="96" />
            <img src="/images/qualibat-logo-small.webp" alt="Qualibat RGE" className="h-12 md:h-16 w-auto" width="132" height="96" />
          </div>

          {/* Titre principal */}
          <div className="text-center mb-12">
            <div className="inline-block bg-[#E6A835] text-white px-6 py-2 rounded-full text-sm font-bold mb-4">
              Arrêté Septembre 2025
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Simulateur MaPrimeRénov' + CEE
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              Découvrez en 4 minutes si vous êtes éligible
            </p>
            <p className="text-lg text-gray-500">
              Jusqu'à <span className="font-bold text-[#2D9A68]">14 158€</span> d'aides
            </p>
          </div>

          {/* Barre de progression simple */}
          <div className="mb-8 pb-8 border-b-2 border-gray-200">
            <p className="text-center text-gray-600 font-semibold mb-4">
              Étape {step}/{totalSteps}
            </p>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#2D9A68] transition-all duration-300"
                style={{ width: `${(step / totalSteps) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Formulaire */}
          <div>
            {/* Step 1: Logement */}
            {step === 1 && (
              <div className="space-y-10" data-testid="step-1">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">🏠 Votre logement</h2>
                  <p className="text-gray-600 text-lg">Étape 1/4 - Quelques informations</p>
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-4">Code postal</label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => handleInputChange('postal_code', e.target.value)}
                    className="w-full px-6 py-5 text-xl border-2 border-gray-300 rounded-xl focus:border-[#2D9A68] focus:outline-none"
                    placeholder="Ex: 75001"
                    maxLength="5"
                    data-testid="input-postal-code"
                  />
                  {errors.postal_code && <p className="text-red-500 mt-2">{errors.postal_code}</p>}
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-6">Vous habitez ?</label>
                  <div className="grid grid-cols-2 gap-6">
                    {housingTypes.map((type) => (
                      <button
                        key={type.value}
                        onClick={() => handleInputChange('housing_type', type.value)}
                        className={`relative p-10 border-4 rounded-2xl transition-all ${
                          formData.housing_type === type.value
                            ? type.value === 'appartement' 
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-[#2D9A68] bg-[#2D9A68]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`housing-type-${type.value}`}
                      >
                        <div className="text-7xl mb-4">{type.emoji}</div>
                        <div className="text-xl font-bold text-gray-900">{type.label}</div>
                        <div className="text-sm text-gray-500 mt-2">{type.description}</div>
                        {formData.housing_type === type.value && type.value !== 'appartement' && (
                          <div className="absolute top-4 right-4 w-8 h-8 bg-[#2D9A68] text-white rounded-full flex items-center justify-center text-xl">
                            ✓
                          </div>
                        )}
                        {formData.housing_type === type.value && type.value === 'appartement' && (
                          <div className="absolute top-4 right-4 w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-xl">
                            ⚠
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  {errors.housing_type && <p className="text-red-500 mt-2 text-center">{errors.housing_type}</p>}
                  
                  {/* Avertissement pour appartement - bloque la progression */}
                  {formData.housing_type === 'appartement' && (
                    <div className="mt-6 bg-gradient-to-r from-orange-50 to-blue-50 border-2 border-orange-400 p-8 rounded-xl shadow-lg">
                      <div className="text-center mb-4">
                        <div className="text-5xl mb-3">🏢</div>
                        <p className="text-2xl font-bold text-gray-900 mb-2">Vous êtes en copropriété</p>
                      </div>
                      <p className="text-gray-800 mb-4 text-center text-lg">
                        Les travaux de rénovation énergétique en appartement nécessitent <strong>l'accord du syndic de copropriété</strong> et de l'assemblée générale.
                      </p>
                      <p className="text-gray-700 mb-6 text-center">
                        Notre équipe vous accompagne pour constituer votre dossier et présenter votre projet au syndic.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                          href="/contact"
                          className="bg-[#2F5BA0] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#1e3a66] transition-all text-center inline-flex items-center justify-center gap-2"
                        >
                          📞 Nous contacter
                        </a>
                        <a
                          href="tel:0188617508"
                          className="bg-[#2D9A68] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#1e5c43] transition-all text-center inline-flex items-center justify-center gap-2"
                        >
                          ☎️ 01 88 61 75 08
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-4">Surface habitable (m²)</label>
                  <input
                    type="number"
                    value={formData.surface}
                    onChange={(e) => handleInputChange('surface', e.target.value)}
                    className="w-full px-6 py-5 text-xl border-2 border-gray-300 rounded-xl focus:border-[#2D9A68] focus:outline-none"
                    placeholder="Ex: 100"
                    min="20"
                    data-testid="input-surface"
                  />
                  {errors.surface && <p className="text-red-500 mt-2">{errors.surface}</p>}
                </div>
              </div>
            )}

            {/* Step 2: Revenus */}
            {step === 2 && (
              <div className="space-y-10" data-testid="step-2">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">👥 Votre foyer</h2>
                  <p className="text-gray-600 text-lg">Étape 2/4 - Pour calculer vos aides</p>
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-6 text-center">Combien de personnes dans votre foyer ?</label>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
                    {occupantsOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleInputChange('occupants', option.value)}
                        className={`relative p-8 border-4 rounded-2xl transition-all hover:scale-105 ${
                          formData.occupants === option.value
                            ? 'border-[#2D9A68] bg-[#2D9A68]/5 scale-105'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`occupants-${option.value}`}
                      >
                        <div className="text-6xl mb-2">{option.emoji}</div>
                        <div className="text-2xl font-bold text-gray-900">{option.label}</div>
                        <div className="text-sm text-gray-600">{option.description}</div>
                        {formData.occupants === option.value && (
                          <div className="absolute top-3 right-3 w-7 h-7 bg-[#2D9A68] text-white rounded-full flex items-center justify-center text-lg">
                            ✓
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  {errors.occupants && <p className="text-red-500 mt-2 text-center">{errors.occupants}</p>}
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded-lg">
                  <p className="text-blue-800 text-center">
                    <strong>💡 Info :</strong> Le revenu fiscal de référence se trouve sur votre dernier avis d'imposition
                  </p>
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-4 text-center">Revenu fiscal de référence (€)</label>
                  <input
                    type="number"
                    value={formData.fiscal_reference_income}
                    onChange={(e) => handleInputChange('fiscal_reference_income', e.target.value)}
                    className="w-full px-6 py-5 text-xl border-2 border-gray-300 rounded-xl focus:border-[#2D9A68] focus:outline-none text-center"
                    placeholder="Ex: 35 000"
                    min="0"
                    data-testid="input-income"
                  />
                  {errors.fiscal_reference_income && <p className="text-red-500 mt-2 text-center">{errors.fiscal_reference_income}</p>}
                </div>
              </div>
            )}

            {/* Step 3: Chauffage et Travaux */}
            {step === 3 && (
              <div className="space-y-10" data-testid="step-3">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">🔥 Chauffage & Travaux</h2>
                  <p className="text-gray-600 text-lg">Étape 3/4 - Vos besoins</p>
                </div>

                {/* Message spécial appartement */}
                {formData.housing_type === 'appartement' && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
                    <p className="text-blue-900 font-bold mb-3">🏢 Vous êtes en copropriété</p>
                    <p className="text-blue-800 mb-3">
                      Les travaux de rénovation énergétique en appartement nécessitent systématiquement <strong>l'accord du syndic</strong> et de l'assemblée générale des copropriétaires.
                    </p>
                    <p className="text-blue-800 mb-4">
                      Nous vous recommandons de <strong>nous contacter directement</strong> pour étudier la faisabilité de votre projet avec votre syndic.
                    </p>
                    <a
                      href="/contact"
                      className="inline-block bg-[#2F5BA0] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#1e3a66] transition-all"
                    >
                      📞 Nous contacter
                    </a>
                  </div>
                )}

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-6">Votre mode de chauffage principal ?</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {heatingTypes.map((type) => (
                      <button
                        key={type.value}
                        onClick={() => handleInputChange('current_heating', type.value)}
                        className={`relative p-8 border-4 rounded-2xl transition-all ${
                          formData.current_heating === type.value
                            ? 'border-[#2D9A68] bg-[#2D9A68]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`heating-type-${type.value}`}
                      >
                        <div className="text-5xl mb-3">{type.emoji}</div>
                        <div className="text-lg font-bold text-gray-900">{type.label}</div>
                        {formData.current_heating === type.value && (
                          <div className="absolute top-3 right-3 w-7 h-7 bg-[#2D9A68] text-white rounded-full flex items-center justify-center text-lg">
                            ✓
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  {errors.current_heating && <p className="text-red-500 mt-2 text-center">{errors.current_heating}</p>}
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-6">
                    Quel(s) travaux souhaitez-vous réaliser ? (plusieurs choix possibles)
                  </label>
                  
                  {/* Avertissements selon le chauffage */}
                  {formData.current_heating === 'electrique' && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-5 rounded-lg mb-6">
                      <p className="text-yellow-800">
                        <strong>⚠️ Chauffage électrique :</strong> La PAC Air-Air est plus adaptée car pas besoin de circuit d'eau. La PAC Air-Eau nécessiterait l'installation de radiateurs à eau ou plancher chauffant.
                      </p>
                    </div>
                  )}
                  
                  {formData.current_heating === 'bois' && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-5 rounded-lg mb-6">
                      <p className="text-yellow-800">
                        <strong>⚠️ Chauffage bois :</strong> La PAC Air-Air est recommandée en complément. La PAC Air-Eau nécessiterait un circuit d'eau dédié.
                      </p>
                    </div>
                  )}
                  
                  {(formData.current_heating === 'gaz' || formData.current_heating === 'fioul') && (
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded-lg mb-6">
                      <p className="text-blue-800">
                        <strong>✅ Chauffage {formData.current_heating === 'gaz' ? 'gaz' : 'fioul'} :</strong> La PAC Air-Eau est idéale pour remplacer votre chaudière ! Attention : vos radiateurs doivent être compatibles basse température ou vous devrez les changer.
                      </p>
                    </div>
                  )}
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    {workTypes.map((work) => {
                      // Déterminer si l'option doit être désactivée ou afficher un warning
                      const isPacAirEau = work.value === 'pac_air_eau';
                      const isPacAirAir = work.value === 'pac_air_air';
                      const isBallonSolaire = work.value === 'ballon_solaire';
                      const isBallonThermo = work.value === 'ballon_thermodynamique';
                      const isIsolationCombles = work.value === 'isolation_combles';
                      const isIsolationPlanchers = work.value === 'isolation_planchers';
                      const isVentilation = work.value === 'ventilation';
                      const isAppartement = formData.housing_type === 'appartement';
                      
                      const isNotRecommended = isPacAirEau && (formData.current_heating === 'electrique' || formData.current_heating === 'bois');
                      
                      // Désactiver TOUTES les options pour appartement (nécessite accord syndic)
                      const isDisabledForAppartement = isAppartement;
                      
                      return (
                        <div key={work.value} className="relative">
                          <button
                            onClick={() => !isDisabledForAppartement && handleWorkTypeToggle(work.value)}
                            disabled={isDisabledForAppartement}
                            className={`w-full relative p-6 border-4 rounded-2xl text-left transition-all ${
                              isDisabledForAppartement
                                ? 'border-gray-200 bg-gray-100 opacity-40 cursor-not-allowed'
                                : formData.work_types.includes(work.value)
                                ? 'border-[#2D9A68] bg-[#2D9A68]/5'
                                : isNotRecommended
                                ? 'border-gray-300 bg-gray-50 opacity-60'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            data-testid={`work-type-${work.value}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className="text-4xl">{work.emoji}</div>
                              <div className="flex-1 font-bold text-gray-900">{work.label}</div>
                              {formData.work_types.includes(work.value) && !isDisabledForAppartement && (
                                <div className="w-7 h-7 bg-[#2D9A68] text-white rounded-full flex items-center justify-center flex-shrink-0 text-lg">
                                  ✓
                                </div>
                              )}
                            </div>
                            
                            {/* Avantages et Inconvénients */}
                            {formData.work_types.includes(work.value) && !isDisabledForAppartement && workTypesDetails[work.value] && (
                              <div className="mt-4 pt-4 border-t-2 border-gray-200">
                                <div className="grid md:grid-cols-2 gap-4">
                                  {/* Avantages */}
                                  <div>
                                    <p className="font-bold text-green-700 mb-2 flex items-center gap-2">
                                      <span className="text-lg">✓</span> Avantages
                                    </p>
                                    <ul className="space-y-1">
                                      {workTypesDetails[work.value].avantages.map((avantage, idx) => (
                                        <li key={idx} className="text-sm text-green-700 flex items-start gap-2">
                                          <span className="flex-shrink-0">•</span>
                                          <span>{avantage}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                  
                                  {/* Inconvénients */}
                                  <div>
                                    <p className="font-bold text-orange-700 mb-2 flex items-center gap-2">
                                      <span className="text-lg">✗</span> Inconvénients
                                    </p>
                                    <ul className="space-y-1">
                                      {workTypesDetails[work.value].inconvenients.map((inconvenient, idx) => (
                                        <li key={idx} className="text-sm text-orange-700 flex items-start gap-2">
                                          <span className="flex-shrink-0">•</span>
                                          <span>{inconvenient}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            )}
                          </button>
                          
                          {isNotRecommended && !isDisabledForAppartement && (
                            <div className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                              Non recommandé
                            </div>
                          )}
                          
                          {isDisabledForAppartement && (
                            <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                              Copropriété
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {errors.work_types && <p className="text-red-500 mt-2 text-center">{errors.work_types}</p>}
                </div>
              </div>
            )}

            {/* Step 4: Contact */}
            {step === 4 && (
              <div className="space-y-10" data-testid="step-4">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">📞 Vos coordonnées</h2>
                  <p className="text-gray-600 text-lg">Étape 4/4 - Pour recevoir vos résultats</p>
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-4">Nom et Prénom</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-6 py-5 text-xl border-2 border-gray-300 rounded-xl focus:border-[#2D9A68] focus:outline-none"
                    placeholder="Jean Dupont"
                    data-testid="input-name"
                  />
                  {errors.name && <p className="text-red-500 mt-2">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-4">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-6 py-5 text-xl border-2 border-gray-300 rounded-xl focus:border-[#2D9A68] focus:outline-none"
                    placeholder="jean.dupont@email.com"
                    data-testid="input-email"
                  />
                  {errors.email && <p className="text-red-500 mt-2">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-4">Téléphone (optionnel)</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-6 py-5 text-xl border-2 border-gray-300 rounded-xl focus:border-[#2D9A68] focus:outline-none"
                    placeholder="06 12 34 56 78"
                    data-testid="input-phone"
                  />
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 p-5 rounded-lg">
                  <p className="text-green-800 flex items-start gap-3">
                    <Shield size={24} className="flex-shrink-0 mt-1" />
                    <span>Vos données sont sécurisées et ne seront jamais partagées. Conformité RGPD garantie.</span>
                  </p>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-12 pt-8 border-t-2 border-gray-200">
              {step > 1 && (
                <button
                  onClick={handlePrev}
                  className="px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-bold text-lg hover:bg-gray-50 transition-all"
                  data-testid="prev-button"
                >
                  ← Précédent
                </button>
              )}
              
              {/* Cacher le bouton suivant si appartement sélectionné à l'étape 1 */}
              {!(step === 1 && formData.housing_type === 'appartement') && (
                <button
                  onClick={handleNext}
                  disabled={loading}
                  className="ml-auto bg-[#2D9A68] text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-[#237852] transition-all disabled:opacity-50"
                  data-testid="next-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="animate-spin" size={24} />
                      Calcul en cours...
                    </span>
                  ) : step === totalSteps ? (
                    'Calculer mes aides →'
                  ) : (
                    'Suivant →'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EligibilityTest;